import React from 'react'
import { v4 as uuidv4 } from 'uuid'


export const isFlutterApp = () => {
  if (localStorage.getItem('flutterSimulation') === 'true') {
    return true
  }
  return typeof window?.AppBridge !== 'undefined'
}
export const isIOSApp = () => {
  return window?.isIOSApp
}
export const isAndroidApp = () => {
  return window?.isAndroidApp
}

export const getAppVersion = () => {
  return new Promise((resolve) => {
    const get_version_callback = (parsed: { app_version: string, bridge_version: string }) => {
      resolve(parsed)
    }
    MessageToFlutter({
      action: FLUTTER_ACTIONS.GET_VERSION,
      data: {},
      callbackFunction: get_version_callback,
    })
  })
}

export const MessageToFlutter = (object: {
  action: (typeof FLUTTER_ACTIONS)[keyof typeof FLUTTER_ACTIONS]
  data: {
    [key: string]: number | string | boolean | number[]
  }
  callbackFunction?: (data: any) => void
  permanentFunctionName?: string // for use of notification listener
}) => {
  try {
    if (isFlutterApp()) {
      let messageObject = { ...object }
      let functionName = ''

      if (typeof object?.callbackFunction === 'function') {
        functionName = `flutter_callback_` + uuidv4().replace(/-/g, '_')
        // @ts-ignore
        window[functionName] = (data: string) => {
          const parsed = JSON.parse(data)
          object.callbackFunction?.(parsed)
          if (!object?.permanentFunctionName) {
            delete (window as any)[functionName]
          }
        }
      } else if (object?.permanentFunctionName) {
        functionName = object?.permanentFunctionName
      }
      messageObject.callbackFunction = functionName as any
      const json = JSON.stringify(messageObject)
      window?.AppBridge?.postMessage?.(json)
    }
  } catch (error) {
    window.console.error('MessageToFlutter', error)
  }
}

export const MessageToWeb = (json: string) => {
  if (isFlutterApp()) {
    return JSON.parse(json)
  }
}


export const FLUTTER_ACTIONS = {
  // Google Sign-In authentication - data: {}, callbackFunction: string, returns: {idt: string}
  GOOGLE_LOGIN: 'google_login',

  // Apple Sign-In authentication - data: {}, callbackFunction: string, returns: {idt: string}
  APPLE_LOGIN: 'apple_login',

  // Get Firebase Cloud Messaging token - data: {}, callbackFunction: string, returns: {fcm_token: string}
  GET_FCM_TOKEN: 'get_fcm_token',

  // Pick files from device storage - data: {accept?: string, multiple?: boolean}, callbackFunction: string
  PICK_FILE: 'pick_file',

  // Update app theme color - data: {hex_color: string}, no callback response
  UPDATE_THEME_COLOR: 'update_theme_color',

  // Get current platform information - data: {}, callbackFunction: string, returns: {platform: string}
  GET_PLATFORM: 'get_platform',

  // Sign out user and clear FCM token - data: {}, no callback response
  SIGN_OUT: 'sign_out',

  // Get app version information - data: {}, callbackFunction: string, returns: {app_version: string, bridge_version: string}
  GET_VERSION: 'get_version',

  // Start audio recording - data: {}, callbackFunction: string, returns: {status: "recording"}
  RECORD_AUDIO: 'record_audio',

  // Stop audio recording and get recorded audio - data: {}, callbackFunction: string, returns: {status: "stopped", audio: string}
  STOP_RECORDING: 'stop_recording',

  // Abort current audio recording - data: {}, no callback response
  ABORT_RECORDING: 'abort_recording',

  // Trigger device vibration - data: {durationInMilliSeconds?: number, amplitude?: number, pattern?: number[], intensities?: number[], repeat?: number}, callbackFunction: string, returns: {status: "vibrated", ...}
  VIBRATE: 'vibrate',

  // Download file from URL to device - data: {url: string, filename?: string}, callbackFunction: string, returns: {status: "downloaded" | "error", url: string, filename: string, error?: string, supported: boolean}
  DOWNLOAD_FILE: 'download_file',

  // Get list of supported bridge actions - data: {}, callbackFunction: string, returns: {actions: string[], supported: boolean}
  GET_SUPPORTED_ACTIONS: 'get_supported_actions',

  OPEN_URL: 'open_url',
  // Open URL in browser - data: {url: string}, no callback response

  // Register a permanent listener for incoming voice-call push events pushed by
  // native (FCM). data: {}, permanentFunctionName: string. Native invokes the
  // listener with a JSON call_update payload: {event_name:'call_update',
  // call_status, customer_id, sender_id?, sdp_answer?, contact?}
  LISTEN_VOICE_CALLS: 'listen_voice_calls',

  // Inform native of a voice call state change so it can manage the call
  // notification + keep the webview alive (Android foreground service).
  // data: {status: 'connected'|'on_hold'|'ended', customer_id: string}, no callback
  VOICE_CALL_STATE: 'voice_call_state',
}

// Permanent window function native calls to push incoming voice-call events.
export const VOICE_CALL_LISTENER = 'flutter_permanent_callback_voice_call'

/**
 * Register a permanent listener so native can push incoming-call payloads into
 * the webview. Returns an unregister function. No-op outside the Flutter app.
 */
export const registerVoiceCallListener = (
  handler: (payload: unknown) => void
): (() => void) => {
  if (!isFlutterApp()) {
    return () => undefined
  }
  window[VOICE_CALL_LISTENER] = (data: string) => {
    try {
      handler(JSON.parse(data))
    } catch (error) {
      window.console.error('voiceCallListener', error)
    }
  }
  MessageToFlutter({
    action: FLUTTER_ACTIONS.LISTEN_VOICE_CALLS,
    data: {},
    permanentFunctionName: VOICE_CALL_LISTENER,
  })
  return () => {
    delete window[VOICE_CALL_LISTENER]
  }
}

/** Tell native the call's state changed (notification + keep-alive). No-op on web. */
export const notifyVoiceCallState = (
  status: string,
  customerId: string
): void => {
  MessageToFlutter({
    action: FLUTTER_ACTIONS.VOICE_CALL_STATE,
    data: { status, customer_id: customerId },
  })
}

export const openUrlInDefaultBrowser = (url: string, e?: React.MouseEvent<HTMLAnchorElement>) => {
  if (isFlutterApp() && url?.length) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    MessageToFlutter({
      action: FLUTTER_ACTIONS.OPEN_URL,
      data: {
        url,
      },
    })
    return true
  }
  return false
}

export const openUrlWrapperForFlutterApp = (e: React.MouseEvent<HTMLAnchorElement>) => {
  const url = (e?.currentTarget as HTMLAnchorElement)?.href as string
  if (url?.length) {
    return openUrlInDefaultBrowser(url, e)
  }
  return false
}